import React, { useState } from "react";
import {
  FileText,
  Phone,
  FileCheck2,
  Send,
  ClipboardCheck,
  Layers,
  Printer,
  ShieldCheck,
  PackageCheck,
  Package,
  ChevronDown,
} from "lucide-react";

const API_URL = process.env.REACT_APP_API_URL;

// One icon per workflow step, so the trail reads at a glance instead of
// every step looking like the same generic dot.
const STEP_ICONS = {
  "New RFP": FileText,
  "Contacted": Phone,
  "Quote Prepared": FileCheck2,
  "Quote Sent": Send,
  "PO Received": ClipboardCheck,
  "Pre-Press": Layers,
  "In Production": Printer,
  "In QC": ShieldCheck,
  "In Packaging": PackageCheck,
  "Shipped & Completed": Package,
};

// Shown when the admin hasn't left a remark for that step — so the
// customer still sees a plain-English line instead of nothing.
const STEP_FALLBACK_TEXT = {
  "New RFP": "Your enquiry has been received.",
  "Contacted": "Our team has reached out to you.",
  "Quote Prepared": "A quote has been prepared for your requirements.",
  "Quote Sent": "The quote has been sent to you.",
  "PO Received": "Your purchase order has been received.",
  "Pre-Press": "Your files are being prepared for print.",
  "In Production": "Your order is in production.",
  "In QC": "Your order is in quality checking.",
  "In Packaging": "Your order is being packed.",
  "Shipped & Completed": "Your order has shipped and is complete.",
};

// Collapsed to this many of the most recent steps; "Show more events"
// reveals the rest, same as a courier's tracking page.
const COLLAPSED_COUNT = 3;

const TrackTimeline = ({ timeline }) => {
  const [expanded, setExpanded] = useState(false);

  if (!timeline?.length) return null;

  const formatDate = (value) => {
    if (!value) return null;
    try {
      return new Date(value).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return null;
    }
  };

  const formatTime = (value) => {
    if (!value) return null;
    try {
      return new Date(value).toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return null;
    }
  };

  // Most recent step first, like a shipment-tracking feed.
  const steps = [...timeline].reverse();
  const visibleSteps = expanded ? steps : steps.slice(0, COLLAPSED_COUNT);
  const hasMore = steps.length > COLLAPSED_COUNT;

  return (
    <>
      <ol className="tk-timeline">
        {visibleSteps.map((step, index) => {
          const isLast = index === visibleSteps.length - 1;
          const date = formatDate(step.date);
          const time = formatTime(step.date);
          const Icon = STEP_ICONS[step.status] ?? Package;

          return (
            <li key={step.status} className={`tk-step tk-step--${step.state}`}>
              {!isLast && <span className="tk-step-line" aria-hidden="true" />}

              <span className="tk-step-marker" aria-hidden="true">
                <Icon size={13} strokeWidth={2.2} />
              </span>

              <div className="tk-step-body">
                <p className="tk-step-label">{step.status}</p>
                {date && (
                  <p className="tk-step-date">
                    {date}
                    {time && (
                      <>
                        <span className="tk-step-dot" aria-hidden="true">|</span>
                        {time}
                      </>
                    )}
                  </p>
                )}

                <p className="tk-step-desc">
                  {step.remarks || STEP_FALLBACK_TEXT[step.status] || ""}
                </p>

                {step.photos?.length > 0 && (
                  <div className="tk-step-photos">
                    {step.photos.map((photo) => (
                      <a
                        key={photo.url}
                        href={`${API_URL}/${photo.url}`}
                        target="_blank"
                        rel="noreferrer"
                        className="tk-step-photo"
                      >
                        <img
                          src={`${API_URL}/${photo.url}`}
                          alt={`${step.status} progress`}
                          loading="lazy"
                        />
                      </a>
                    ))}
                  </div>
                )}
              </div>

              <span className="sr-only">
                {step.isCurrent ? "Current status" : "Completed"}
              </span>
            </li>
          );
        })}
      </ol>

      {hasMore && (
        <button
          type="button"
          className="tk-show-more"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? "Show less" : "Show more events"}
          <ChevronDown
            size={15}
            strokeWidth={2.4}
            className={`tk-show-more-icon ${expanded ? "tk-show-more-icon--up" : ""}`}
          />
        </button>
      )}
    </>
  );
};

export default TrackTimeline;
